import React, { useState, useRef } from 'react';
import { Anime, SEASON_CONFIGS, TIER_CONFIGS } from '../types/anime';
import {
  DEFAULT_ANIME_COVER,
  formatImageUrl,
  getDayConfig,
  getAnimePlatformSlots,
  isAiringToday,
  isOnBreak,
  isUpdatedToday,
} from '../utils/helpers';
import { MalScoreBadge } from './MalScoreBadge';
import { EpisodeProgress } from './EpisodeProgress';
import { sameDataProps } from '../utils/memo';
import { airingState, recentlyEndedDaysLeft } from '../utils/airingStatus';
import {
  Minus,
  Plus,
  Pencil,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Bell,
  BellOff,
} from 'lucide-react';

interface AnimeCardProps {
  anime: Anime;
  index?: number;
  isSortMode: boolean;
  isDragging?: boolean;
  isDropTarget?: boolean;
  onQuickPlus: (id: string) => void;
  onQuickMinus: (id: string) => void;
  onToggleNotify: (id: string) => void;
  onEdit: (anime: Anime) => void;
  onOpenDetail: (anime: Anime) => void;
  onMoveUp?: (id: string) => void;
  onMoveDown?: (id: string) => void;
  onDragStart?: (e: React.DragEvent, id: string) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, id: string) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onTouchStartDrag?: (e: React.TouchEvent, anime: Anime) => void;
  onStartSortMode?: () => void;
  /** Show the tier grade on the cover (used outside the tier board) */
  showGrade?: boolean;
}

export const AnimeCard: React.FC<AnimeCardProps> = React.memo(({
  anime,
  index = 0,
  isSortMode,
  isDragging = false,
  isDropTarget = false,
  onQuickPlus,
  onQuickMinus,
  onToggleNotify,
  onEdit,
  onOpenDetail,
  onMoveUp,
  onMoveDown,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onTouchStartDrag,
  onStartSortMode,
  showGrade = false,
}) => {
  const [imgError, setImgError] = useState(false);
  const [isPressingPlus, setIsPressingPlus] = useState(false);
  const [isPressingMinus, setIsPressingMinus] = useState(false);

  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);

  const watchedEp = Number(anime.watchedEp) || 0;

  const seasonCfg = SEASON_CONFIGS[anime.season] || SEASON_CONFIGS.FALL;
  const dayCfg = getDayConfig(anime.broadcastDay);
  const updatedToday = isUpdatedToday(anime);
  const airingToday = isAiringToday(anime);

  const coverSrc = imgError
    ? DEFAULT_ANIME_COVER
    : formatImageUrl(anime.imageUrl);

  const handlePlus = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPressingPlus(true);
    setTimeout(() => setIsPressingPlus(false), 200);
    onQuickPlus(anime.id);
  };

  const handleMinus = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (watchedEp <= 0) return;
    setIsPressingMinus(true);
    setTimeout(() => setIsPressingMinus(false), 200);
    onQuickMinus(anime.id);
  };

  // Touch handling for iOS-style Long Press & Drag
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };

    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }

    if (!isSortMode && onStartSortMode) {
      longPressTimerRef.current = setTimeout(() => {
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
          navigator.vibrate(30);
        }
        onStartSortMode();
        onTouchStartDrag?.(e, anime);
      }, 450);
    } else if (isSortMode && onTouchStartDrag) {
      longPressTimerRef.current = setTimeout(() => {
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
          navigator.vibrate(20);
        }
        onTouchStartDrag(e, anime);
      }, 120);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartPosRef.current && longPressTimerRef.current) {
      const touch = e.touches[0];
      if (touch) {
        const dx = Math.abs(touch.clientX - touchStartPosRef.current.x);
        const dy = Math.abs(touch.clientY - touchStartPosRef.current.y);
        if (dx > 8 || dy > 8) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
      }
    }
  };

  const handleTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  let cardClass = 'border-white/[0.06] hover:border-white/[0.16] hover:-translate-y-0.5';
  if (isDragging) {
    cardClass =
      'opacity-25 border-dashed border-amber-400 ring-2 ring-amber-400/40 scale-95';
  } else if (isDropTarget) {
    cardClass =
      'ios-drop-target ring-2 ring-amber-400 border-amber-400 bg-amber-500/15 scale-95';
  } else if (isSortMode) {
    const jiggleIndex = index % 4;
    cardClass = `ios-jiggle-${jiggleIndex} cursor-grab active:cursor-grabbing border-dashed border-amber-400/80 ring-1 ring-amber-400/30 hover:border-amber-300`;
  }

  // Only show the Thai name when it adds something the main title doesn't already say
  const thaiTitle = (anime.thaiTitle || '').trim();
  const norm = (t: string) => t.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
  const showThai =
    thaiTitle !== '' &&
    !norm(anime.title).includes(norm(thaiTitle).slice(0, 8)) &&
    !norm(thaiTitle).includes(norm(anime.title).slice(0, 8));

  // Just ended: stays with the airing shows for 3 weeks, with a countdown on the cover
  const endedState = airingState(anime);
  const endedDaysLeft = recentlyEndedDaysLeft(anime);
  // Finished shows have no next airing, so no day/time on the card
  const scheduleText =
    anime.broadcastDay === 'UNKNOWN' || endedState !== 'airing'
      ? null
      : `${dayCfg.shortTh}${anime.broadcastTime ? ` ${anime.broadcastTime}` : ''}`;
  // Skipping this week on AnimeRuka: say when it's back instead of the usual slot
  const breakLabel = endedState === 'airing' && isOnBreak(anime) ? anime.onBreak!.label : null;

  const slots = getAnimePlatformSlots(anime);
  // Only the platforms this show is actually on
  const platforms = [
    { key: 'A', name: 'Ani-One', slot: slots.aniOne, on: 'bg-red-500/90 text-white' },
    { key: 'B', name: 'Bilibili', slot: slots.bilibili, on: 'bg-sky-500/90 text-white' },
    { key: 'W', name: 'Anime-Waku', slot: slots.waku, on: 'bg-teal-500/90 text-white' },
    { key: 'R', name: 'AnimeRuka', slot: slots.ruka, on: 'bg-orange-500/90 text-white' },
  ].flatMap((p) => (p.slot ? [{ ...p, slot: p.slot }] : []));

  return (
    <div
      data-anime-id={anime.id}
      draggable={isSortMode}
      onDragStart={(e) => onDragStart && onDragStart(e, anime.id)}
      onDragOver={(e) => onDragOver && onDragOver(e)}
      onDrop={(e) => onDrop && onDrop(e, anime.id)}
      onDragEnd={(e) => onDragEnd && onDragEnd(e)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onContextMenu={(e) => {
        if (isSortMode) e.preventDefault();
      }}
      className={`anime-card group relative flex flex-col w-full bg-[#0d1020] border rounded-2xl overflow-hidden shadow-md shadow-black/25 transition-[transform,border-color,opacity] duration-200 select-none ${cardClass}`}
    >
      {/* Cover */}
      <div className="relative aspect-[3/4] w-full bg-slate-950 overflow-hidden">
        <button
          type="button"
          disabled={isSortMode}
          onClick={() => !isSortMode && onOpenDetail(anime)}
          title={
            isSortMode
              ? 'กดค้างหรือลากเพื่อย้ายตำแหน่ง'
              : 'แตะเพื่อดูรายการตอน ลิงก์ดู และเรื่องย่อ'
          }
          className={`w-full h-full block focus:outline-none relative ${
            isSortMode ? 'cursor-grab active:cursor-grabbing' : ''
          }`}
        >
          <img
            src={coverSrc}
            alt={anime.title}
            onError={() => setImgError(true)}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover object-center group-hover:scale-[1.04] transition-transform duration-300 pointer-events-none"
          />
          {/* Soft fades so badges stay readable on any poster */}
          <div className="absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-black/55 to-transparent pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
        </button>

        {/* Top-left: season + broadcast day (pink when it airs / updated today) */}
        {!isSortMode && (
          <div className="absolute top-1.5 left-1.5 z-10 flex flex-col items-start gap-1 pointer-events-none">
            <span
              title={`${seasonCfg.nameTh} ${anime.year}`}
              className="inline-flex items-center gap-0.5 px-1.5 py-[3px] rounded-md text-[9px] sm:text-[10.5px] font-semibold leading-none bg-black/70 text-white/90 ring-1 ring-white/10"
            >
              {seasonCfg.icon} {anime.year}
            </span>
            {endedState === 'recent' && endedDaysLeft !== null && (
              <span
                title={`ฉายจบแล้ว — อีก ${endedDaysLeft} วันจะย้ายไปหมวด "จบแล้ว"`}
                className="inline-flex items-center px-1.5 py-[3px] rounded-md text-[9px] sm:text-[10.5px] font-semibold leading-none bg-sky-500 text-white shadow-[0_0_10px_rgba(14,165,233,0.45)] whitespace-nowrap"
              >
                🏁 เพิ่งจบ · อีก {endedDaysLeft} วัน
              </span>
            )}
            {endedState === 'ended' && (
              <span className="inline-flex items-center px-1.5 py-[3px] rounded-md text-[9px] sm:text-[10.5px] font-semibold leading-none bg-black/70 text-slate-300 ring-1 ring-white/10">
                ✓ จบแล้ว
              </span>
            )}
            {breakLabel && !updatedToday && (
              <span
                title={`AnimeRuka: สัปดาห์นี้งดฉาย / พักฉาย · ปกติฉาย ${scheduleText || '-'}`}
                className="inline-flex items-center px-1.5 py-[3px] rounded-md text-[9px] sm:text-[10.5px] font-semibold leading-none bg-amber-500 text-slate-950 whitespace-nowrap"
              >
                ⏸ พักฉาย · {breakLabel}
              </span>
            )}
            {!(breakLabel && !updatedToday) && (scheduleText || airingToday || updatedToday) && (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-[3px] rounded-md text-[9px] sm:text-[10.5px] font-semibold leading-none ${
                  airingToday || updatedToday
                    ? 'bg-rose-500 text-white shadow-[0_0_12px_rgba(244,63,94,0.6)]'
                    : 'bg-black/70 text-white/90 ring-1 ring-white/10'
                }`}
              >
                {updatedToday ? 'ตอนใหม่' : airingToday ? 'วันนี้' : null}
                {(airingToday || updatedToday) && scheduleText && anime.broadcastTime
                  ? ` ${anime.broadcastTime}`
                  : !airingToday && !updatedToday
                  ? scheduleText
                  : null}
              </span>
            )}
          </div>
        )}

        {/* Top-right: LINE bell */}
        {!isSortMode && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleNotify(anime.id);
            }}
            title={
              anime.notifyLine
                ? 'เปิดแจ้งเตือน LINE อยู่ (คลิกเพื่อปิด)'
                : 'ปิดแจ้งเตือน LINE อยู่ (คลิกเพื่อเปิด)'
            }
            className={`absolute top-1.5 right-1.5 z-10 w-6 h-6 sm:w-7 sm:h-7 rounded-full grid place-items-center transition-all active:scale-90 ${
              anime.notifyLine
                ? 'bg-[#06C755] text-white shadow-[0_0_10px_rgba(6,199,85,0.5)]'
                : 'bg-black/65 text-white/70 hover:text-white ring-1 ring-white/10'
            }`}
          >
            {anime.notifyLine ? (
              <Bell className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-current" />
            ) : (
              <BellOff className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            )}
          </button>
        )}

        {/* Sort Mode Controls */}
        {isSortMode && (
          <div className="absolute top-1.5 left-1.5 z-20 flex flex-col gap-1">
            <div
              className="bg-amber-500 text-slate-950 p-1 rounded-md shadow-md flex items-center justify-center cursor-grab active:cursor-grabbing"
              title="ลากเพื่อย้าย"
            >
              <GripVertical className="w-3.5 h-3.5" />
            </div>
            {onMoveUp && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveUp(anime.id);
                }}
                className="bg-slate-900/90 hover:bg-amber-500 text-amber-300 hover:text-slate-950 p-1 rounded-md shadow border border-amber-400/40 active:scale-90 transition-transform"
                title="ย้ายขึ้น"
              >
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
            )}
            {onMoveDown && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveDown(anime.id);
                }}
                className="bg-slate-900/90 hover:bg-amber-500 text-amber-300 hover:text-slate-950 p-1 rounded-md shadow border border-amber-400/40 active:scale-90 transition-transform"
                title="ย้ายลง"
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        {/* Bottom of cover: score + audio */}
        <div className="absolute bottom-2 inset-x-1.5 z-10 flex items-end justify-between gap-1 pointer-events-none">
          <MalScoreBadge
            title={anime.malTitle || anime.title}
            year={anime.malTitle ? 0 : anime.year}
            fallback={anime.score}
            compact
            className="text-[9px] sm:text-[10.5px] !bg-black/75 !border-amber-300/30"
          />
          {showGrade && (
            <span
              className={`ml-auto px-1.5 py-[3px] rounded-md text-[9px] sm:text-[10.5px] font-bold leading-none text-white bg-gradient-to-br ${TIER_CONFIGS[anime.grade]?.gradient || ''}`}
            >
              {anime.grade}
            </span>
          )}
          <span
            className={`${showGrade ? 'card-audio ' : 'ml-auto '}px-1.5 py-[3px] rounded-md text-[8.5px] sm:text-[10px] font-semibold leading-none whitespace-nowrap ${
              anime.audioType === 'ซับ+พากย์'
                ? 'bg-amber-400 text-slate-950'
                : anime.audioType === 'พากย์ไทย'
                ? 'bg-sky-500 text-white'
                : 'bg-black/70 text-white ring-1 ring-white/15'
            }`}
          >
            {anime.audioType === 'ซับ+พากย์' ? 'ซับ+พากย์' : anime.audioType === 'พากย์ไทย' ? 'พากย์' : 'ซับ'}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-2 sm:p-2.5 flex flex-col flex-grow gap-1.5">
        <div className="min-h-[2.4em] sm:min-h-0">
          <h4
            title={anime.thaiTitle ? `${anime.title} — ${anime.thaiTitle}` : anime.title}
            className={`font-medium text-[11px] sm:text-[13px] text-slate-100 leading-snug ${
              showThai ? 'line-clamp-2 sm:line-clamp-1' : 'line-clamp-2'
            }`}
          >
            {anime.title}
          </h4>
          {showThai && (
            <p className="hidden sm:block text-[11px] text-slate-500 line-clamp-1 leading-snug mt-0.5">
              {thaiTitle}
            </p>
          )}
        </div>

        {/* Watched / aired / total */}
        <div className="mt-auto">
          <EpisodeProgress anime={anime} />
        </div>

        {/* Platforms + actions */}
        <div
          className={`flex items-center gap-1 pt-1.5 border-t border-white/[0.05] transition-opacity ${
            isSortMode ? 'opacity-30 pointer-events-none' : ''
          }`}
        >
          <div className="card-dots flex items-center gap-[3px]">
            {platforms.map(({ key, name, slot, on }) => (
              <a
                key={key}
                href={slot.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title={`เปิดดูบน ${name}`}
                className={`w-4 h-4 sm:w-[18px] sm:h-[18px] rounded-[5px] grid place-items-center text-[8.5px] sm:text-[10px] font-bold hover:brightness-125 transition ${on}`}
              >
                {key}
              </a>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-[3px]">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(anime);
              }}
              disabled={isSortMode}
              title="แก้ไขข้อมูลเรื่องนี้"
              className="card-edit w-6 h-6 rounded-md place-items-center text-slate-500 hover:text-white hover:bg-white/[0.08] transition-colors"
            >
              <Pencil className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={handleMinus}
              disabled={watchedEp <= 0 || isSortMode}
              title="ลดตอนที่ดูแล้ว (-1)"
              className={`card-minus w-[22px] h-[22px] sm:w-6 sm:h-6 rounded-md place-items-center bg-white/[0.05] text-slate-300 hover:bg-white/[0.1] disabled:opacity-30 disabled:pointer-events-none transition-all active:scale-90 ${
                isPressingMinus ? 'scale-90 bg-rose-500/40' : ''
              }`}
            >
              <Minus className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={handlePlus}
              disabled={isSortMode}
              title="เพิ่มตอนที่ดูแล้ว (+1)"
              className={`w-[22px] h-[22px] sm:w-6 sm:h-6 rounded-md grid place-items-center bg-fuchsia-500/80 text-white hover:bg-fuchsia-400 transition-all active:scale-90 shadow-[0_2px_10px_-2px_rgba(217,70,239,0.6)] ${
                isPressingPlus ? 'scale-110' : ''
              }`}
            >
              <Plus className="w-3 h-3" strokeWidth={3} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}, sameDataProps);

